import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell, Loader2, CheckCheck } from 'lucide-react';
import { useClientAuth } from '../../contexts/ClientAuthContext';
import { usePortalNotifications, useMarkNotificationsRead } from '../../hooks/usePortal';

export default function ClientNotificationsPage() {
  const { refreshUser } = useClientAuth();
  const { t } = useTranslation();
  const { data, isLoading } = usePortalNotifications();
  const markReadMutation = useMarkNotificationsRead();
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const notifications = data?.notifications ?? [];
  const unread = data?.unread ?? 0;

  const markAllRead = async () => {
    await markReadMutation.mutateAsync(undefined);
    refreshUser();
  };

  const openNotification = (n: { id: number; is_read: number }) => {
    setExpandedId(prev => (prev === n.id ? null : n.id));
    if (!n.is_read) {
      markReadMutation.mutate([n.id]);
      refreshUser();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-teal-500" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Bell className="text-amber-500" size={24} />
            {t('portal.notificationsPage.title')}
          </h1>
          <p className="text-slate-500 mt-1">
            {unread > 0 ? t('portal.notificationsPage.unread', { count: unread }) : t('portal.notificationsPage.allRead')}
          </p>
        </div>
        {unread > 0 && (
          <button
            onClick={markAllRead}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-teal-600 hover:text-teal-700 border border-teal-200 rounded-lg hover:bg-teal-50 transition-colors"
          >
            <CheckCheck size={16} />
            {t('portal.notificationsPage.markAllRead')}
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {notifications.length === 0 ? (
          <p className="px-5 py-16 text-slate-400 text-center text-sm">{t('portal.notificationsPage.empty')}</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {notifications.map(n => {
              const expanded = expandedId === n.id;
              return (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => openNotification(n)}
                  className={`w-full text-left px-5 py-4 flex items-start gap-3 transition-colors ${n.is_read ? '' : 'bg-teal-50/30'} hover:bg-slate-50`}
                >
                  <div className={`mt-1 w-2.5 h-2.5 rounded-full shrink-0 ${n.is_read ? 'bg-slate-200' : 'bg-teal-500'}`} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${n.is_read ? 'text-slate-700' : 'font-medium text-slate-900'}`}>{n.title}</p>
                    {n.message && <p className={`text-sm text-slate-500 mt-0.5 ${expanded ? 'whitespace-pre-line' : 'line-clamp-2'}`}>{n.message}</p>}
                    <p className="text-xs text-slate-400 mt-1">
                      {new Date(n.created_at).toLocaleDateString('es-ES', {
                        day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                      })}
                    </p>
                    {expanded && n.link && (
                      <Link
                        to={n.link}
                        onClick={(e) => e.stopPropagation()}
                        className="inline-block mt-2 text-xs font-medium text-teal-600 hover:text-teal-700"
                      >
                        {t('portal.notificationsPage.viewDetail', 'Ver detalle')} →
                      </Link>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
